import { Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Iconify from 'src/components/iconify/iconify';
import Image from 'src/components/image/image';
import SvgColor from 'src/components/svg-color';
import { Upload } from 'src/components/upload';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { createNewInterview } from 'src/services/interview.service';
import PropTypes from 'prop-types';
import { CandidateProfileContext } from './context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  disqualifiedDiv: {
    padding: '8px 10px',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    borderRadius: '6px',
  },
}));

const STATES_MAP = {
  INITIAL: { value: 'INITIAL', heading: 'No file found. Upload resume' },
  GENERATE_PROFILE: { value: 'GENERATE_PROFILE', button: 'Generate Profile' },
  GENERATING_PROFILE: {
    value: 'GENERATING_PROFILE',
    button: 'Generating Profile',
  },
  RESUME_PARSING_FAILED: { value: 'RESUME_PARSING_FAILED' },
  SUCCESS: { value: 'SUCCESS' },
};

const NoResumeFound = ({ jobId }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { obj: candidateDetails, saveChanges: refetchDetails } = useContext(
    CandidateProfileContext,
  );

  console.log(candidateDetails);
  // states
  const [currentState, setCurrentState] = useState(STATES_MAP.INITIAL.value);
  const [resumeFile, setResumeFile] = useState(null);

  // functions
  const handleMultipleDrop = acceptedFiles => {
    const newFile = Object.assign(acceptedFiles[0], {
      preview: URL.createObjectURL(acceptedFiles[0]),
    });

    setResumeFile(newFile);
    setCurrentState(STATES_MAP.GENERATE_PROFILE.value);
  };

  const handleGenerateProfile = useCallback(async () => {
    try {
      setCurrentState(STATES_MAP.GENERATING_PROFILE.value);
      const folderName = uuidv4();
      const resumeUrl = await uploadResumeToS3(resumeFile, `${folderName}`);

      const reqBody = {
        name: candidateDetails?.candidate_info?.name,
        email: candidateDetails?.candidate_info?.email,
        phone_no: candidateDetails?.candidate_info?.phone_number,
        should_send_email: `false`,
        valid_for: '7',
        resume_url: resumeUrl,
      };

      const interviewResponse = await createNewInterview(reqBody, jobId);
      if (interviewResponse.success === true) {
        refetchDetails();
      } else throw new Error(interviewResponse.message);
    } catch (err) {
      console.log(err);
      setCurrentState(STATES_MAP.GENERATE_PROFILE.value);
    }
  }, [
    candidateDetails?.candidate_info?.email,
    candidateDetails?.candidate_info?.name,
    candidateDetails?.candidate_info?.phone_number,
    jobId,
    refetchDetails,
    resumeFile,
  ]);

  // effects
  return (
    <Stack spacing="20px">
      {STATES_MAP[currentState]?.heading && (
        <Stack className={classes.disqualifiedDiv}>
          <Image src="/assets/resume-error.svg" />
          <Typography variant="subheading1">
            {STATES_MAP[currentState].heading}
          </Typography>
        </Stack>
      )}
      <Upload
        sx={{
          pt: '66px',
          pb: '66.8px',
        }}
        file={resumeFile}
        onDrop={handleMultipleDrop}
        multiple={false}
        accept={{
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        }}
      />
      {STATES_MAP[currentState]?.button && (
        <Stack sx={{ width: '100%', marginTop: '2rem' }}>
          <LoadingButton
            variant="contained"
            loadingPosition="end"
            loading={currentState === STATES_MAP.GENERATING_PROFILE.value}
            startIcon={<SvgColor src="/assets/icons/job/ic_star_twinkle.svg" />}
            onClick={handleGenerateProfile}
          >
            {STATES_MAP[currentState].button}
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
};

export default NoResumeFound;

NoResumeFound.propTypes = {
  jobId: PropTypes.string.isRequired,
};
